import { NotificationTemplateSource, type OfferNotificationTemplateChannel } from '@today/types'
import { EmptyState, Table, type TableColumn } from '@today/ui-components'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { DEFAULT_TEMPLATE_CHANNEL_LABELS, type NotificationTemplateRowView } from './notification-template-types'

/** The table's built-in copy; override any entry for localisation. */
export interface NotificationTemplateTableLabels {
	caption: string
	name: string
	channel: string
	locale: string
	status: string
	isDefault: string
	source: string
	actions: string
	active: string
	inactive: string
	defaultFlag: string
	notDefault: string
	core: string
	tenant: string
	edit: string
	delete: string
	empty: string
	deleteTitle: string
	deleteBody: string
	deleteConfirm: string
	deleteCancel: string
}

const DEFAULT_LABELS: NotificationTemplateTableLabels = {
	caption: 'Notification templates',
	name: 'Name',
	channel: 'Channel',
	locale: 'Locale',
	status: 'Status',
	isDefault: 'Default',
	source: 'Source',
	actions: 'Actions',
	active: 'Active',
	inactive: 'Inactive',
	defaultFlag: 'Default',
	notDefault: '—',
	core: 'Core',
	tenant: 'Tenant',
	edit: 'Edit',
	delete: 'Delete',
	empty: 'No notification templates yet.',
	deleteTitle: 'Delete this template?',
	deleteBody: 'Offers that use it cannot be sent until their message is changed.',
	deleteConfirm: 'Delete template',
	deleteCancel: 'Cancel',
}

/**
 * Props for {@link NotificationTemplateTable}.
 *
 * @property rows - The templates in library order; the table never reorders them.
 * @property pendingDeleteId - The template whose delete awaits confirmation, or null.
 */
export interface NotificationTemplateTableProps {
	rows: readonly NotificationTemplateRowView[]
	pendingDeleteId: string | null
	onEdit: (id: string) => void
	onDeleteRequest: (id: string) => void
	onDeleteConfirm: () => void
	onDeleteCancel: () => void
	channelLabels?: Partial<Record<OfferNotificationTemplateChannel, string>>
	labels?: Partial<NotificationTemplateTableLabels>
}

type Row = NotificationTemplateRowView

/** The columns: the five REQ-011 facts, then the row actions. */
function columnsOf(props: NotificationTemplateTableProps, labels: NotificationTemplateTableLabels): TableColumn<Row>[] {
	const channels = { ...DEFAULT_TEMPLATE_CHANNEL_LABELS, ...props.channelLabels }
	return [
		{ key: 'name', header: labels.name },
		{ key: 'channelType', header: labels.channel, cell: (row) => channels[row.channelType] },
		{ key: 'locale', header: labels.locale },
		{ key: 'isActive', header: labels.status, cell: (row) => (row.isActive ? labels.active : labels.inactive) },
		{
			key: 'isDefaultForChannel',
			header: labels.isDefault,
			cell: (row) => (row.isDefaultForChannel ? labels.defaultFlag : labels.notDefault),
		},
		{
			key: 'source',
			header: labels.source,
			cell: (row) => (row.source === NotificationTemplateSource.CORE ? labels.core : labels.tenant),
		},
		{
			key: 'actions',
			header: labels.actions,
			cell: (row) => (
				<span className="flex gap-2">
					<Button size="sm" variant="secondary" onPress={() => props.onEdit(row.id)}>
						{labels.edit}
					</Button>
					<Button size="sm" variant="muted" onPress={() => props.onDeleteRequest(row.id)}>
						{labels.delete}
					</Button>
				</span>
			),
		},
	]
}

/** The delete confirmation for the pending row. */
function DeleteDialog(props: NotificationTemplateTableProps & { labels: NotificationTemplateTableLabels }) {
	const { labels } = props
	const pending = props.rows.find((row) => row.id === props.pendingDeleteId)
	if (!pending) return null
	return (
		<Dialog isOpen onOpenChange={(open) => (open ? undefined : props.onDeleteCancel())} title={labels.deleteTitle}>
			<p className="text-ds-sm font-semibold text-ds-text">{pending.name}</p>
			<p className="text-ds-sm text-ds-text">{labels.deleteBody}</p>
			<div className="mt-4 flex justify-end gap-2">
				<Button variant="secondary" onPress={props.onDeleteCancel}>
					{labels.deleteCancel}
				</Button>
				<Button variant="danger" onPress={props.onDeleteConfirm}>
					{labels.deleteConfirm}
				</Button>
			</div>
		</Dialog>
	)
}

/**
 * The tenant's notification-template library (TO-140 offer-notification-templates
 * REQ-011): name, channel type, locale, active status, default flag and source,
 * with Edit and a confirmed Delete per row. Pure: rows, the pending delete and the
 * callbacks are props.
 */
export function NotificationTemplateTable(props: NotificationTemplateTableProps): ReactElement {
	const labels = { ...DEFAULT_LABELS, ...props.labels }
	return (
		<>
			<Table
				aria-label={labels.caption}
				columns={columnsOf(props, labels)}
				rows={[...props.rows]}
				emptyState={<EmptyState title={labels.empty} />}
			/>
			<DeleteDialog {...props} labels={labels} />
		</>
	)
}
